import '../../i18n/autoTranslate';
import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ResourcePreviewModal = ({
  resource,
  isOpen,
  onClose,
  onStartPractice,
  onBookmark
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isBookmarked, setIsBookmarked] = useState(resource?.isBookmarked || false);

  useEffect(() => {
    if (!resource) return;
    setActiveTab(resource?.youtubeId ? 'video' : 'overview');
    setIsBookmarked(Boolean(resource?.isBookmarked));
  }, [resource?.id]);

  const tabs = useMemo(() => {
    const out = [{ id: 'overview', label: 'Overview', icon: 'Eye' }];

    if (resource?.youtubeId) {
      out.push({ id: 'video', label: 'Video', icon: 'Play' });
    }

    if (resource?.questions?.length) {
      out.push({ id: 'questions', label: 'Questions', icon: 'MessageSquareText' });
    }

    out.push({ id: 'content', label: 'Plan', icon: 'FileText' });

    if (resource?.reviews?.length) {
      out.push({ id: 'reviews', label: 'Reviews', icon: 'Star' });
    }

    return out;
  }, [resource]);

  if (!isOpen || !resource) return null;

  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked);
    if (onBookmark) {
      onBookmark(resource?.id, !isBookmarked);
    }
  };

  const renderStars = (rating = 0) => {
    const safeRating = Math.round(Number(rating) || 0);
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={14}
        className={index < safeRating ? 'text-[#8B7CFF]' : 'text-white/25'}
        fill={index < safeRating ? 'currentColor' : 'none'}
      />
    ));
  };

  const getContentTypeIcon = (type) => {
    switch (type) {
      case 'video':
        return 'Play';
      case 'text':
        return 'FileText';
      case 'interactive':
        return 'MousePointer';
      case 'practice':
        return 'Target';
      case 'testing':
        return 'Bug';
      default:
        return 'Circle';
    }
  };

  const questions = resource?.questions || [];
  const contentSections = resource?.contentSections || [];
  const learningOutcomes = resource?.learningOutcomes || [
    "Understand the interviewer's evaluation criteria",
    "Practice clear, structured answers",
    "Turn this resource into an AI interview session"
  ];

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="rounded-3xl border border-white/10 bg-[#0B0B12]/95 w-full max-w-5xl max-h-[92vh] overflow-hidden animate-fade-in shadow-[0_30px_120px_rgba(0,0,0,0.65)]">
        {/* Header */}
        <div className="relative">
          <div className="h-48 overflow-hidden">
            <Image
              src={resource?.thumbnail}
              alt={resource?.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B12]/90 via-[#0B0B12]/30 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B12]/70 via-transparent to-transparent" />
          </div>

          {/* Header Content */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center flex-wrap gap-2 mb-2">
                  <span className="px-3 py-1 text-xs font-semibold bg-white/10 border border-white/10 text-white rounded-full">
                    {resource?.type}
                  </span>
                  <span className="px-3 py-1 text-xs font-semibold bg-white/10 text-white/80 rounded-full border border-white/10">
                    {resource?.difficulty}
                  </span>
                  {resource?.role && (
                    <span className="px-3 py-1 text-xs font-semibold bg-[#6C63FF]/20 text-[#D7D4FF] rounded-full border border-[#6C63FF]/25">
                      {resource.role}
                    </span>
                  )}
                  {resource?.interviewType && (
                    <span className="px-3 py-1 text-xs font-semibold bg-emerald-500/15 text-emerald-200 rounded-full border border-emerald-500/20 capitalize">
                      {resource.interviewType}
                    </span>
                  )}
                </div>

                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 line-clamp-2">{resource?.title}</h2>

                <div className="flex items-center flex-wrap gap-x-4 gap-y-2 text-sm text-white/75">
                  <div className="flex items-center space-x-1">
                    <Icon name="Clock" size={16} />
                    <span>{resource?.duration}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Users" size={16} />
                    <span>{resource?.completions}</span>
                  </div>
                  {resource?.rating && (
                    <div className="flex items-center space-x-1">
                      <Icon name="Star" size={16} className="text-[#8B7CFF]" fill="currentColor" />
                      <span>{resource?.rating}</span>
                    </div>
                  )}
                  {resource?.sourceName && (
                    <div className="flex items-center space-x-1">
                      <Icon name="ExternalLink" size={16} />
                      <span>{resource.sourceName}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2 shrink-0">
                <button
                  onClick={handleBookmark}
                  className={`
                    w-10 h-10 rounded-2xl backdrop-blur-sm transition-all duration-200 border border-white/10
                    ${isBookmarked
                      ? 'bg-white text-black' :'bg-black/30 text-white hover:bg-white hover:text-black'
                    }
                    flex items-center justify-center
                  `}
                >
                  <Icon name="Star" size={18} fill={isBookmarked ? "currentColor" : "none"} />
                </button>

                <button
                  onClick={onClose}
                  className="w-10 h-10 rounded-2xl bg-black/30 backdrop-blur-sm text-white hover:bg-white hover:text-black transition-all duration-200 flex items-center justify-center border border-white/10"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-white/10 overflow-x-auto">
          <div className="flex items-center px-6 min-w-max">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`
                  flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-all duration-200
                  ${activeTab === tab?.id
                    ? 'text-white border-white/70' :'text-white/60 border-transparent hover:text-white'
                  }
                `}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
                {tab.id === 'questions' && (
                  <span className="rounded-full bg-white/10 border border-white/10 px-2 py-0.5 text-[11px]">
                    {questions.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto max-h-[48vh]">
          {activeTab === 'overview' && (
            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Description</h3>
                <p className="text-white/70 leading-relaxed">
                  {resource?.description}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                  <div className="text-xs text-white/50 mb-1">Role</div>
                  <div className="text-sm font-semibold text-white">{resource?.role || 'All Roles'}</div>
                </div>
                <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                  <div className="text-xs text-white/50 mb-1">Interview Type</div>
                  <div className="text-sm font-semibold text-white capitalize">{resource?.interviewType || 'mixed'}</div>
                </div>
                <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                  <div className="text-xs text-white/50 mb-1">Practice Questions</div>
                  <div className="text-sm font-semibold text-white">{questions.length || '—'}</div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3">What you'll learn</h3>
                <ul className="space-y-2">
                  {learningOutcomes?.map((item, index) => (
                    <li key={index} className="flex items-start space-x-2 text-white/70">
                      <Icon name="Check" size={16} className="text-emerald-300 mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {resource?.tags?.length ? (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {resource.tags.map((tag) => (
                      <span key={tag} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/70">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Prerequisites</h3>
                <p className="text-white/70">
                  {resource?.prerequisites || "No prior experience required. Basic understanding of interview processes is helpful."}
                </p>
              </div>
            </div>
          )}

          {activeTab === 'video' && resource?.youtubeId && (
            <div className="p-6 space-y-5">
              <div className="rounded-3xl overflow-hidden border border-white/10 bg-black aspect-video">
                <iframe
                  title={resource?.title}
                  src={`https://www.youtube.com/embed/${resource.youtubeId}`}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>

              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 p-4">
                <div>
                  <div className="font-semibold text-white">Real YouTube resource</div>
                  <div className="text-sm text-white/60">
                    Watch the lesson, then launch an AI interview using the questions attached to this resource.
                  </div>
                </div>
                {resource?.youtubeUrl && (
                  <a
                    href={resource.youtubeUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-2xl bg-white/10 border border-white/10 text-white hover:bg-white/15 transition"
                  >
                    <Icon name="ExternalLink" size={16} />
                    Open on YouTube
                  </a>
                )}
              </div>
            </div>
          )}

          {activeTab === 'questions' && (
            <div className="p-6 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-white">Real practice questions</h3>
                  <p className="text-sm text-white/60 mt-1">
                    These are passed to the interview setup page when you click Start Interview.
                  </p>
                </div>
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70">
                  {questions.length} questions
                </span>
              </div>

              {questions.length ? (
                <div className="space-y-3">
                  {questions.map((item, index) => (
                    <div key={item.id || index} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <div className="flex items-center flex-wrap gap-2 mb-3">
                        <span className="rounded-full bg-white/10 border border-white/10 px-2.5 py-1 text-[11px] text-white/70">
                          Q{index + 1}
                        </span>
                        <span className="rounded-full bg-[#6C63FF]/15 border border-[#6C63FF]/20 px-2.5 py-1 text-[11px] text-[#D7D4FF]">
                          {item.role || resource?.role}
                        </span>
                        <span className="rounded-full bg-emerald-500/10 border border-emerald-500/15 px-2.5 py-1 text-[11px] text-emerald-200 capitalize">
                          {item.type || resource?.interviewType}
                        </span>
                        <span className="rounded-full bg-white/5 border border-white/10 px-2.5 py-1 text-[11px] text-white/55">
                          {item.difficulty || resource?.difficulty}
                        </span>
                      </div>

                      <h4 className="text-base font-semibold text-white leading-relaxed">
                        {item.question}
                      </h4>

                      {item.evaluationFocus && (
                        <p className="mt-3 text-sm text-white/60">
                          <span className="text-white/85 font-medium">Evaluation focus:</span> {item.evaluationFocus}
                        </p>
                      )}

                      {item.answerFramework && (
                        <div className="mt-3 rounded-2xl bg-black/20 border border-white/10 p-3">
                          <div className="text-xs uppercase tracking-wide text-white/45 mb-1">Answer framework</div>
                          <p className="text-sm text-white/70">{item.answerFramework}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-white/60">
                  No questions attached to this resource yet.
                </div>
              )}
            </div>
          )}

          {activeTab === 'content' && (
            <div className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Practice Plan</h3>
              <div className="space-y-3">
                {(contentSections.length ? contentSections : [
                  { title: "Review the resource", duration: "10 min", type: "text", completed: false },
                  { title: "Practice role-specific questions", duration: "20 min", type: "practice", completed: false },
                  { title: "Launch AI interview", duration: "15 min", type: "interactive", completed: false }
                ])?.map((section, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors duration-150">
                    <div className={`
                      w-9 h-9 rounded-2xl flex items-center justify-center border border-white/10
                      ${section?.completed ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/5 text-white/70'}
                    `}>
                      {section?.completed ? (
                        <Icon name="Check" size={16} />
                      ) : (
                        <Icon name={getContentTypeIcon(section?.type)} size={16} />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-white">{section?.title}</h4>
                      <div className="flex items-center space-x-2 text-sm text-white/60">
                        <span>{section?.duration}</span>
                        <span>•</span>
                        <span className="capitalize">{section?.type}</span>
                      </div>
                    </div>

                    {section?.completed && (
                      <Icon name="CheckCircle" size={20} className="text-emerald-300" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">Reviews</h3>
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1">
                    {renderStars(resource?.rating)}
                  </div>
                  <span className="text-sm text-white/60">
                    {resource?.rating} ({resource?.reviews?.length || 0} reviews)
                  </span>
                </div>
              </div>

              {resource?.reviews?.length ? (
                <div className="space-y-4">
                  {resource.reviews.map((review, index) => (
                    <div key={review?.id || index} className="flex space-x-3 p-4 rounded-2xl bg-white/5 border border-white/10">
                      <Image
                        src={review?.avatar}
                        alt={review?.user}
                        className="w-10 h-10 rounded-full"
                      />

                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-white">{review?.user}</h4>
                          <span className="text-xs text-white/50">{review?.date}</span>
                        </div>

                        <div className="flex items-center space-x-1 mb-2">
                          {renderStars(review?.rating)}
                        </div>

                        <p className="text-sm text-white/70">{review?.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-white/60">
                  Reviews will appear here after users complete this resource.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center flex-wrap gap-4">
              {resource?.isPremium && (
                <div className="flex items-center space-x-2 text-sm text-white/80">
                  <Icon name="Crown" size={16} />
                  <span>Premium Content</span>
                </div>
              )}

              {resource?.isDownloadable && (
                <div className="flex items-center space-x-2 text-sm text-white/80">
                  <Icon name="Download" size={16} />
                  <span>Offline Available</span>
                </div>
              )}

              {questions.length > 0 && (
                <div className="flex items-center space-x-2 text-sm text-white/80">
                  <Icon name="MessageSquareText" size={16} />
                  <span>{questions.length} questions attached</span>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="border-white/10 text-white/70 hover:text-white hover:bg-white/5"
              >
                Close
              </Button>

              <Button
                variant="default"
                onClick={() => onStartPractice && onStartPractice(resource)}
                iconName="Play"
                iconPosition="left"
              >
                Start Interview
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourcePreviewModal;
